import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from '../../axios';
import useAuth from '../hooks/useAuth';
import { BOOKING_NOTIFICATION } from '../api';
import { wsPathUrl } from '../utils/config';
import { tenantWebSocketPath } from '../utils/tenantApi';

const getNotificationList = (payload) => {
  const candidates = [
    payload?.data?.data,
    payload?.data?.notifications,
    payload?.data?.results,
    payload?.data,
    payload?.notifications,
    payload?.results,
    payload,
  ];
  return candidates.find(Array.isArray) || [];
};

const getSocketNotifications = (rawMessage) => {
  try {
    const payload = JSON.parse(rawMessage);
    const list = getNotificationList(payload);
    if (list.length) return list;
    const nestedData = payload?.data && typeof payload.data === 'object' && !Array.isArray(payload.data)
      ? payload.data
      : {};
    const source = nestedData?.notification || payload?.notification || nestedData || payload;
    const notification = source && typeof source === 'object' && !Array.isArray(source)
      ? {
          ...source,
          id: source?.id ?? source?.notification_id,
          notification_id: source?.notification_id ?? source?.id,
          title: payload?.title || source?.title,
          message: payload?.body || source?.message || source?.body,
          body: payload?.body || source?.body || source?.message,
          event_type: source?.event_type || source?.event,
          created_at: source?.created_at || payload?.created_at || new Date().toISOString(),
          mark_as_read: source?.mark_as_read ?? false,
        }
      : null;
    return notification && typeof notification === 'object' && !Array.isArray(notification)
      ? [notification]
      : [];
  } catch (error) {
    console.error('Invalid notification socket message.', error);
    return [];
  }
};

const isNotificationSeen = (notification) => Boolean(
  notification?.mark_as_read ??
  notification?.is_seen ??
  notification?.seen ??
  notification?.is_read ??
  notification?.read,
);

const mergeNotifications = (current, incoming) => {
  const merged = [...incoming, ...current];
  const seenIds = new Set();
  return merged.filter((notification, index) => {
    const key = notification?.id ?? notification?.notification_id;
    if (key === null || key === undefined) return index < incoming.length;
    if (seenIds.has(String(key))) return false;
    seenIds.add(String(key));
    return true;
  });
};

const appendNotifications = (current, incoming) => {
  const merged = [...current, ...incoming];
  const seenIds = new Set();
  return merged.filter((notification) => {
    const key = notification?.id ?? notification?.notification_id;
    if (key === null || key === undefined) return true;
    if (seenIds.has(String(key))) return false;
    seenIds.add(String(key));
    return true;
  });
};

const markNotificationSeenLocally = (notification) => ({
  ...notification,
  mark_as_read: true,
  is_seen: true,
  seen: true,
  is_read: true,
  read: true,
});

const NotificationContext = createContext({
  notifications: [],
  unseenCount: 0,
  loading: false,
  error: false,
  hasMore: false,
  loadingMore: false,
  deleteNotification: async () => {},
  clearNotifications: async () => {},
  getNotifications: async () => {},
  markNotificationSeen: async () => {},
  markAllNotificationsSeen: async () => {},
  loadMoreNotifications: async () => {},
});

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [nextPage, setNextPage] = useState(null);
  const reconnectTimerRef = useRef(null);

  const getNotifications = useCallback(async (page = 1, append = false) => {
    if (!isAuthenticated) return;
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(false);
    try {
      const response = await axios.get(`${BOOKING_NOTIFICATION}?page=${page}&per_page=20`);
      const incoming = getNotificationList(response?.data);
      setNotifications((current) => append ? appendNotifications(current, incoming) : incoming);
      setNextPage(response?.data?.last_page ? null : response?.data?.next_page || null);
    } catch (requestError) {
      console.error(requestError);
      setError(true);
    } finally {
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  }, [isAuthenticated]);

  const loadMoreNotifications = useCallback(async () => {
    if (!nextPage || loadingMore) return;
    await getNotifications(nextPage, true);
  }, [getNotifications, loadingMore, nextPage]);

  const deleteNotification = useCallback(async (notificationId) => {
    await axios.delete(`${BOOKING_NOTIFICATION}/${notificationId}`);
    setNotifications((current) => current.filter(
      (notification) => String(notification?.id ?? notification?.notification_id) !== String(notificationId),
    ));
  }, []);

  const clearNotifications = useCallback(async () => {
    await axios.delete(BOOKING_NOTIFICATION);
    setNotifications([]);
    setNextPage(null);
  }, []);

  const markNotificationSeen = useCallback(async (notificationId) => {
    const target = notifications.find(
      (notification) => String(notification?.id ?? notification?.notification_id) === String(notificationId),
    );
    if (!target || isNotificationSeen(target)) return;
    await axios.post(`${BOOKING_NOTIFICATION}/${notificationId}/seen`);
    setNotifications((current) => current.map((notification) => (
      String(notification?.id ?? notification?.notification_id) === String(notificationId)
        ? markNotificationSeenLocally(notification)
        : notification
    )));
  }, [notifications]);

  const markAllNotificationsSeen = useCallback(async () => {
    if (!notifications.some((notification) => !isNotificationSeen(notification))) return;
    await axios.post(`${BOOKING_NOTIFICATION}/seen`);
    setNotifications((current) => current.map(markNotificationSeenLocally));
  }, [notifications]);

  useEffect(() => {
    if (!isAuthenticated) {
      setNotifications([]);
      return;
    }
    getNotifications();
  }, [getNotifications, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let socket;
    let cancelled = false;

    const connect = () => {
      const token = window.localStorage.getItem('accessToken');
      if (!token || cancelled) return;
      let socketPath;
      try {
        socketPath = tenantWebSocketPath('notifications');
      } catch (error) {
        return;
      }
      socket = new WebSocket(`${wsPathUrl}${socketPath}?token=${encodeURIComponent(token)}`);
      socket.onmessage = (event) => {
        const incoming = getSocketNotifications(event.data);
        if (incoming.length) setNotifications((current) => mergeNotifications(current, incoming));
      };
      socket.onerror = () => socket?.close();
      socket.onclose = () => {
        if (!cancelled) reconnectTimerRef.current = window.setTimeout(connect, 5000);
      };
    };

    connect();
    return () => {
      cancelled = true;
      if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
      socket?.close();
    };
  }, [isAuthenticated]);

  const unseenCount = notifications.filter((notification) => !isNotificationSeen(notification)).length;
  const value = useMemo(() => ({
    notifications,
    unseenCount,
    loading,
    error,
    hasMore: Boolean(nextPage),
    loadingMore,
    deleteNotification,
    clearNotifications,
    getNotifications,
    markNotificationSeen,
    markAllNotificationsSeen,
    loadMoreNotifications,
  }), [
    clearNotifications,
    deleteNotification,
    error,
    getNotifications,
    loadMoreNotifications,
    loading,
    loadingMore,
    markAllNotificationsSeen,
    markNotificationSeen,
    notifications,
    nextPage,
    unseenCount,
  ]);

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export default NotificationContext;
